import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import './AdminLoginPage.css'

export default function SetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [tokenValid, setTokenValid] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Get token and email from URL
  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    // Just check if we have the required URL params - don't verify yet
    if (!token || !email) {
      setError('Invalid link. Please use the link from your invite email.')
    } else {
      setTokenValid(true)
    }
  }, [token, email])

  const validatePassword = (pwd) => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters'
    }
    if (!/[!@#$%^&;*(),.?":{}|<>]/.test(pwd)) {
      return 'Password must contain at least one special character (!;@#$%^&*)'
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter'
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter'
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number'
    }
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    const validationError = validatePassword(password)
    if (validationError) {
      setError(validationError)
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      // Step 1: Verify the token (this creates a session)
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'recovery'
      })

      if (verifyError) {
        throw new Error('Invalid or expired link. Please request a new invite.')
      }

      // Step 2: Update the password
      const { error: updateError } = await supabase.auth.updateUser({ password })

      if (updateError) throw updateError

      // Step 3: Sign out immediately
      await supabase.auth.signOut()

      // Redirect to login page with success message
      navigate('/admin/login?message=Password set successfully. Please log in.')
    } catch (err) {
      setError(err.message)
      // Sign out in case session was created
      await supabase.auth.signOut()
    } finally {
      setLoading(false)
    }
  }

  if (!tokenValid) {
    return (
      <div className="admin-login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>Link Error</h1>
            <p>There was a problem with your invite link</p>
          </div>
          <div className="login-form">
            <div className="error-message">{error || 'Invalid link'}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Set Your Password</h1>
          <p>Create a secure password for your admin account</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm your password"
            />
          </div>

          <p style={{ fontSize: '12px', color: '#6b7280', margin: '0' }}>
            Password must be at least 8 characters with uppercase, lowercase, number, and special character.
          </p>

          <button
            type="submit"
            className="button primary"
            disabled={loading}
          >
            {loading ? 'Setting Password...' : 'Set Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
